import { makePostCall } from "../effects/requests";
import endpoints from "./endpoints";
import { getLocalStorage, setLocalStorage } from "./index";

export type ShopifyBookingBootstrapResult = {
  serviceId: string;
  authenticated: boolean;
  authAttempted: boolean;
};

/**
 * Read Shopify handoff params from the booking app URL.
 */
export function getShopifyBookingQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    serviceId: params.get("service_id") || "",
    shopifyConsumerId: params.get("shopify_consumer_id") || "",
    accessToken: params.get("access_token") || "",
  };
}

/**
 * Remove sensitive auth params from the browser URL after they are stored.
 */
export function stripShopifyAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("access_token");
  url.searchParams.delete("shopify_consumer_id");
  window.history.replaceState({}, "", url.toString());
}

/**
 * Build the Django OAuth authorize URL for a stylist-scoped Shopify login.
 */
export function buildShopifyOAuthLoginUrl(
  stylistUniqueCode: string,
  serviceId?: string
) {
  const base = (endpoints.API_BASE_URL || "").replace(/\/$/, "");
  const authorizePath = (endpoints.shopify_oauth_authorize || "shopify/oauth/authorize/").replace(
    /^\//,
    ""
  );
  const params = new URLSearchParams({
    stylist_unique_code: stylistUniqueCode,
  });
  if (serviceId) {
    params.set("service_id", serviceId);
  }
  return `${base}/${authorizePath}?${params.toString()}`;
}

/**
 * Exchange a logged-in Shopify customer for a HUSL JWT and persist service intent.
 */
export async function bootstrapShopifyBookingAuth(
  stylistUniqueCode: string
): Promise<ShopifyBookingBootstrapResult> {
  const { serviceId, shopifyConsumerId, accessToken } = getShopifyBookingQueryParams();
  const hadAuthParams = Boolean(accessToken || shopifyConsumerId);

  if (accessToken) {
    setLocalStorage("access_token", accessToken);
  }

  if (!getLocalStorage("access_token") && shopifyConsumerId && stylistUniqueCode) {
    try {
      const response: any = await makePostCall({
        url: endpoints.shopify_customer_connect,
        apiPayload: {
          shopify_consumer_id: shopifyConsumerId,
          stylist_unique_code: stylistUniqueCode,
        },
      });
      const token = response?.token?.access;
      if (token) {
        setLocalStorage("access_token", token);
      }
    } catch {
      // Booking can continue as a guest when Shopify connect fails.
    }
  }

  if (hadAuthParams) {
    stripShopifyAuthParamsFromUrl();
  }

  return {
    serviceId,
    authenticated: Boolean(getLocalStorage("access_token")),
    authAttempted: true,
  };
}

/**
 * Find a service in the stylist services API response by id.
 */
export function findServiceInCategories(serviceCategories: any[], serviceId: string) {
  if (!serviceId || !serviceCategories?.length) {
    return null;
  }

  for (let categoryIndex = 0; categoryIndex < serviceCategories.length; categoryIndex += 1) {
    const services = serviceCategories[categoryIndex]?.services_names || [];
    for (let serviceIndex = 0; serviceIndex < services.length; serviceIndex += 1) {
      if (String(services[serviceIndex]?.id) === String(serviceId)) {
        return {
          categoryIndex,
          serviceIndex,
          service: services[serviceIndex],
        };
      }
    }
  }

  return null;
}

/**
 * Build the selectedServices store entry for one pre-selected service.
 */
export function buildSelectedServiceEntry(service: any, sequenceNumber = 1) {
  const entry: any = {
    serviceid: service.id,
    name: service.name,
    amount: service.price,
    duration: service.duration,
    sequence_number: sequenceNumber,
  };

  if (service.has_processing_time) {
    entry.has_processing_time = service.has_processing_time;
    entry.processing_time = service.processing_time;
    entry.application_time = service.application_time;
    entry.finishing_time = service.finishing_time;
  }

  return entry;
}
